import defaultMagicBuildingCurves from "./theMagicBuildingCurves_default.json";

// low / medium / high are authoring levels for the approximately 0-10 chart scale.
// Numeric values are still used internally by the SVG chart, and arrays may mix
// semantic levels with numeric fine-tuning values.
export const CURVE_LEVEL_VALUES = {
  low: 2.5,
  medium: 5.5,
  high: 8.0,
};

const CURVE_LEVEL_VALUE_OVERRIDES = {};
const REQUIRED_DEFAULT_CURVES = {
  tension: 9,
  emotion: 9,
  pacing: 9,
  mystery: 9,
  dialogue: 9,
  sensory_detail: 8,
};

export function resolveCurveValue(value, curveLabel) {
  if (typeof value === "number") return value;

  const normalizedValue = String(value || "").toLowerCase();
  const curveOverrides = CURVE_LEVEL_VALUE_OVERRIDES[curveLabel] || {};

  return curveOverrides[normalizedValue] ?? CURVE_LEVEL_VALUES[normalizedValue] ?? value;
}

function applyCurveCommand(curves, command) {
  const match = String(command || "").match(/^\s*([a-z ]+)\s*\(\s*(\d+)\s*,\s*(low|medium|high)\s*\)\s*$/i);
  if (!match) return curves;

  const [, curveName, sectionText, level] = match;
  const sectionIndex = Number(sectionText) - 1;
  const resolvedLevel = resolveCurveValue(level);

  return curves.map((curve) => {
    if (curve.label.toLowerCase() !== curveName.trim().toLowerCase()) return curve;
    if (sectionIndex < 0 || sectionIndex >= curve.values.length) return curve;

    const values = [...curve.values];
    values[sectionIndex] = resolvedLevel;
    return { ...curve, values };
  });
}

const CURVE_AUTHORING_COMMANDS = [];

function validateDefaultMagicBuildingCurves(defaults) {
  Object.entries(REQUIRED_DEFAULT_CURVES).forEach(([curveId, pointCount]) => {
    const values = defaults.curves?.[curveId];
    if (!Array.isArray(values) || values.length !== pointCount) {
      throw new Error(`Invalid default curve: ${curveId}`);
    }

    if (!values.every((value) => typeof value === "number")) {
      throw new Error(`Default curve must be numeric: ${curveId}`);
    }
  });
}

export function getDefaultMagicBuildingCurves() {
  validateDefaultMagicBuildingCurves(defaultMagicBuildingCurves);

  return {
    ...defaultMagicBuildingCurves,
    curves: Object.fromEntries(
      Object.entries(defaultMagicBuildingCurves.curves).map(([curveId, values]) => [
        curveId,
        values.map((value) => resolveCurveValue(value, curveId)),
      ])
    ),
  };
}

export function resetMagicBuildingCurves() {
  return getDefaultMagicBuildingCurves();
}

const curveIdsByLabel = {
  Tension: "tension",
  Emotion: "emotion",
  Pacing: "pacing",
  Mystery: "mystery",
  Dialogue: "dialogue",
  "Sensory Detail": "sensory_detail",
};

const authoredMagicBuildingCurves = {
  title: "The Magic Building",
  subtitle: "by Shaanzae Shahid",
  description: "",
  glance: [
    { label: "Genre", value: "Classic Short Story", icon: "book" },
    { label: "Reading Time", value: "~12-15 min", icon: "clock" },
    {
      label: "Themes",
      value: "Family, legacy, change, magic, sacrifice",
      icon: "themes",
    },
  ],
  sections: [
    "1. Childhood Memories & The Magic Building (Exposition)",
    "2. Shawna's Rebellion (Rising Action)",
    "3. The Encounter at the Street (Rising Action)",
    "4. The Countdown (Approaching Climax)",
    "5. The Visit & The Magic Show (Climax)",
    "6. Grandpa's Sacrifice (Falling Action)",
    "7. Change of Heart & Resolution (Resolution)",
  ],
  curves: [
    {
      label: "Tension",
      description: "Overall sense of pressure or uncertainty",
      color: "#ff7a18",
      values: defaultMagicBuildingCurves.curves.tension,
    },
    {
      label: "Emotion",
      description: "Depth of feelings and emotional impact",
      color: "#ff4d7e",
      values: defaultMagicBuildingCurves.curves.emotion,
    },
    {
      label: "Pacing",
      description: "Speed of the story's flow",
      color: "#4aa3ff",
      values: defaultMagicBuildingCurves.curves.pacing,
    },
    {
      label: "Mystery",
      description: "What the story hides or reveals.",
      color: "#73d13d",
      values: defaultMagicBuildingCurves.curves.mystery,
    },
    {
      label: "Dialogue",
      description: "Amount of dialogue vs. description",
      color: "#facc15",
      values: defaultMagicBuildingCurves.curves.dialogue,
    },
    {
      label: "Sensory Detail",
      description: "Use of sensory language & imagery",
      color: "#c55cff",
      values: defaultMagicBuildingCurves.curves.sensory_detail,
    },
  ],
  summaries: [
    "Shawna recalls childhood and Grandpa's puppet theater.",
    "Shawna wants the building torn down; she rejects the show.",
    "An angry Shawna meets a kind boy she cannot forget.",
    "As time runs out, Shawna returns to the building.",
    "Shawna sees a magic puppet show made just for her.",
    "Grandpa sold his treasures to save the building.",
    "Shawna sees his love; the magic lives on within her.",
  ],
  notes: [
    {
      title: "Tension builds",
      text: "Demolition nears. Tension peaks at the show, then eases.",
    },
    {
      title: "Emotions grow",
      text: "Nostalgia turns to anger, then love and gratitude.",
    },
    {
      title: "Pacing shifts",
      text: "A calm start builds speed, then slows for reflection.",
    },
    {
      title: "Mystery unfolds",
      text: "Hints build toward the show, where secrets emerge.",
    },
    {
      title: "Dialogue moves meaning",
      text: "Talk drives conflict and reveals relationships.",
    },
    {
      title: "Sensory detail peaks",
      text: "Vivid details bring the building and show to life.",
    },
  ],
  footerNote: "",
};

export const theMagicBuildingCurves = {
  ...authoredMagicBuildingCurves,
  curves: CURVE_AUTHORING_COMMANDS.reduce(
    (curves, command) => applyCurveCommand(curves, command),
    authoredMagicBuildingCurves.curves.map((curve) => ({
      ...curve,
      values: curve.values.map((value) => resolveCurveValue(value, curveIdsByLabel[curve.label] || curve.label)),
    }))
  ),
};

export default theMagicBuildingCurves;
